/**
 * profile-notifications.js
 */

import React from 'react';
import axios from 'axios';
import { Box, Checkbox, Grid, Tooltip, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Controller, FormProvider, useForm, useFieldArray } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { UserContext } from 'contexts/user/user-context';
import Button from 'components/button';
import ConfirmUnsavedEditor from 'components/menu-drawer/confirm-unsaved-editor';
import DrawerActionButton from 'components/menu-drawer/drawer-action-button';
import DrawerContent from 'components/menu-drawer/drawer-content';
import DrawerContentBuilder from 'components/menu-drawer/drawer-content-builder';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import { EditorIconSection } from 'components/icon-section/icon-section';
import MobileDrawerAppBar from 'components/menu-drawer/mobile/mobile-drawer-app-bar';
import { Types, isBitfieldContainingBitmask } from 'utils/constants';


const useStyles = makeStyles(theme => ({
  contactLink: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  confirmEmailTitle: {
    [theme.breakpoints.down('sm')]: {
      fontSize: theme.typography.sizes[4]
    }
  },
  themeModeSection: {
    margin: 0,
    transformOrigin: 'top left',
    transform: 'scale(1.5)',
  },
  centerText: {
    textAlign: 'center',
  },
  destinationHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.sys.onSurfaceVariant,
    fontSize: '1em',
    fontWeight: 700,
    lineHeight: '1.2',
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  hideDestination: {
    display: 'none',
  },
  centerTooltip: {
    textAlign: 'center',
  },
  destinationCheckbox: {
    textAlign: 'center',
  },
}));


const EventChannelSlug = {
  [Types.NotificationEventChannel.UNKNOWN]: null,
  [Types.NotificationEventChannel.INVITATION]: 'invitation',
  [Types.NotificationEventChannel.UPCOMING_TRAINING]: 'upcomingTraining',
  [Types.NotificationEventChannel.EXERCISE_READY_FOR_COMPLETION]: 'exerciseReadyForCompletion',
  [Types.NotificationEventChannel.MANAGER_FEEDBACK]: 'managerFeedback',
  [Types.NotificationEventChannel.RECORD_UPDATE]: 'recordUpdate',
  [Types.NotificationEventChannel.EXERCISE_READY_FOR_COMMENTS]: 'exerciseReadyForComments',
  [Types.NotificationEventChannel.GENERAL_UPDATE]: 'generalUpdate',
  [Types.NotificationEventChannel.VACCINATION_DUE]: 'vaccinationDue',
};


const DestinationCheckboxChannelGroup = (props) => {
  const {
    inputProps,
  } = props;

  const destinationOnChange = destinationGroupValue => valueToSet => {
    inputProps.onChange({
      ...inputProps.value,
      destinations: valueToSet
        ? inputProps.value.destinations | destinationGroupValue
        : inputProps.value.destinations & ~destinationGroupValue,
      isDirty: true,
    })
  };


  return (
    <>
      <DestinationCheckbox
        inputProps={inputProps}
        destinationGroupValue={Types.NotificationDestinationGroupValues.SYSTEM}
        destinationOnChange={destinationOnChange(Types.NotificationDestinationGroupValues.SYSTEM)}
      />
      <DestinationCheckbox
        inputProps={inputProps}
        destinationGroupValue={Types.NotificationDestinationGroupValues.EMAIL}
        destinationOnChange={destinationOnChange(Types.NotificationDestinationGroupValues.EMAIL)}
      />
      <DestinationCheckbox
        inputProps={inputProps}
        destinationGroupValue={Types.NotificationDestinationGroupValues.MOBILE_APP}
        destinationOnChange={destinationOnChange(Types.NotificationDestinationGroupValues.MOBILE_APP)}
      />
    </>
  )
}


const DestinationCheckbox = props => {
  const {
    inputProps,
    destinationGroupValue,
    destinationOnChange,
  } = props;
  const classes = useStyles();


  return (
    <Grid item xs={3} className={`${classes.centerCheckbox} ${destinationGroupValue !== Types.NotificationDestinationGroupValues.MOBILE_APP ? '' : classes.hideDestination}`}>
      <Tooltip
        arrow
        placement="top"
        title={isCheckboxDisabledWithReasonPred(inputProps.value.eventChannel, destinationGroupValue)}
        classes={{ tooltip: classes.centerTooltip }}
      >
        <div className={classes.destinationCheckbox}>
          <Checkbox
            color="primary"
            checked={isBitfieldContainingBitmask(inputProps.value.destinations, destinationGroupValue)}
            disabled={Boolean(isCheckboxDisabledWithReasonPred(inputProps.value.eventChannel, destinationGroupValue))}
            onChange={e => destinationOnChange(e.target.checked)}
          />
        </div>
      </Tooltip>
    </Grid>
  );
}


const isCheckboxDisabledWithReasonPred = (eventChannelId, destination) => {
  if (destination === Types.NotificationDestinationGroupValues.SYSTEM 
    && ![
      Types.NotificationEventChannel.EXERCISE_READY_FOR_COMMENTS, 
    ].includes(eventChannelId)) {
    return `System notifications can't be disabled`;
  } else if (eventChannelId === Types.NotificationEventChannel.GENERAL_UPDATE && destination === Types.NotificationDestinationGroupValues.EMAIL) {
    return `Email notifications for billing are always sent`;
  } else {
    return '';
  }
}


export default function UserEditNotifications(props) {
  const {
    user,
    setUser,
    drawerState,
    onClose
  } = props;
  const { user: currentUser, setUser: setCurrentUser } = React.useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();

  const formContext = useForm({ defaultValues: { ...user } });
  const { reset, handleSubmit, formState } = formContext;
  const { fields: notifPrefFields } = useFieldArray({
    control: formContext.control,
    name: 'notificationPrefs',
    keyName: 'hookFormId',
  });

  const { t } = useTranslation('profileEdit');
  const classes = useStyles();
  const title = 'Notifications';


  const onSubmit = (formData) => {
    save(formData);
  }


  const save = React.useCallback(async (formData) => {
    drawerState.loading.content.set(true);
    const userNotifPrefsToSend = Object.keys(formData.notificationPrefs).map(key => formData.notificationPrefs[key]);
    try {
      const response = await axios({
        method: 'PUT',
        url: `/account/${user.id}/notification-prefs`,
        data: userNotifPrefsToSend,
      });

      const updatedUser = response.data;
      reset(updatedUser);
      setUser(updatedUser);
      enqueueSnackbar('Profile Saved', { variant: 'success' });

      if (user.id === currentUser.id) {
        setCurrentUser(updatedUser);
      }
    } catch (error) {
      enqueueSnackbar(error, { variant: 'error' });
    } finally {
      drawerState.loading.content.set(false);
    }
  }, [drawerState, user, setUser, currentUser, setCurrentUser, enqueueSnackbar, reset]);


  const cancelButton = (
    <Button upperCase={false} onClick={onClose}>Cancel</Button>
  );


  const saveButton = (
    <DrawerActionButton
      level={1}
      disabled={!formState.isDirty}
      style={{ marginLeft: 16 }}
      onClick={() => handleSubmit(save)()}
      children='Save'
    />
  );


  let eventChannelsToDisplay = [];
  if (user.role.isHandler) {
    eventChannelsToDisplay.push(Types.NotificationEventChannel.INVITATION);
    eventChannelsToDisplay.push(Types.NotificationEventChannel.UPCOMING_TRAINING);
    eventChannelsToDisplay.push(Types.NotificationEventChannel.EXERCISE_READY_FOR_COMPLETION);
    eventChannelsToDisplay.push(Types.NotificationEventChannel.MANAGER_FEEDBACK);
    eventChannelsToDisplay.push(Types.NotificationEventChannel.RECORD_UPDATE);
    eventChannelsToDisplay.push(Types.NotificationEventChannel.VACCINATION_DUE);
  }
  if (user.role.isTrainer) {
    eventChannelsToDisplay.push(Types.NotificationEventChannel.EXERCISE_READY_FOR_COMMENTS);
  }
  if (user.role.isHandler || user.role.isTrainer) {
    eventChannelsToDisplay.push(Types.NotificationEventChannel.GENERAL_UPDATE);
  }


  return (
    <>
      <DrawerContentBuilder
        mobileAppBar={{
          content: (
            <MobileDrawerAppBar
              title={title}
              saveButton={saveButton}
              onClose={onClose}
            />
          )
        }}
        desktopHeader={{
          title,
          actions: <>{cancelButton}{saveButton}</>
        }}
        stickyHeader
        children={(
          <FormProvider {...formContext}>
            <form>

              <DrawerContent>
                <EditorIconSection
                  iconName='notifications-edit'
                  iconMarginTop={0}
                  helpProps={{
                    translation: 'profileEdit',
                    section: 'notifications'
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box mb={3}>
                        <Typography variant='subtext-large' sx={{ color: 'inherit' }}>
                          {t('notifications.sectionInfo')}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}></Grid>
                    <Grid item xs={3}>
                      <Typography variant="h6" className={classes.destinationHeader}>
                        {t('notifications.type.system')}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="h6" className={classes.destinationHeader}>
                        {t('notifications.type.email')}
                      </Typography>
                    </Grid>
                    <Grid item xs={2} className={classes.hideDestination}>
                      <Typography variant="h6" className={classes.destinationHeader}>
                        {t('notifications.type.mobileApp')}
                      </Typography>
                    </Grid>
                  </Grid>
                  {notifPrefFields
                    // NOTE: do not filter the pref fields here as the original array indices MUST stay in tact.
                    .map((field, i) => {
                      if (!eventChannelsToDisplay.includes(field.eventChannel)) {
                        return null;
                      }

                      return (
                        <HelpTextItem
                          sidebarText={drawerState.sidebarText}
                          title={t(`notifications.eventChannels.helpTitle`)}
                          text={t(`notifications.eventChannels.helpInfo`)}
                          key={field.hookFormId}
                        >
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Typography variant="subtitle2">
                                {t(`notifications.eventChannels.${EventChannelSlug[field.eventChannel]}.helpTitle`)}
                              </Typography>
                              <Typography variant='subtext-small'>
                                {t(`notifications.eventChannels.${EventChannelSlug[field.eventChannel]}.helpInfo`)}
                              </Typography>
                            </Grid>
                            <Controller
                              control={formContext.control}
                              name={`notificationPrefs.${i}`}
                              defaultValue={field}
                              render={({ field }) => <DestinationCheckboxChannelGroup inputProps={field} />}
                            />
                          </Grid>
                        </HelpTextItem>
                      );
                    })}
                </EditorIconSection>
              </DrawerContent>

            </form>
          </FormProvider>
        )}
      />

      <ConfirmUnsavedEditor
        dirty={formState.isDirty}
        save={async () => handleSubmit(onSubmit)()}
      />
    </>
  )
}